import { CloudUpload, FileText } from "lucide-react";
import React, { useRef } from "react";
import { MIME_TYPES, Dropzone, DropzoneStatus } from "@mantine/dropzone";
import {
  Button,
  Text,
  useMantineTheme,
  MantineTheme,
  Progress,
} from "@mantine/core";
import { FileRejection } from "../hooks/useSecureFileUpload";

interface SecureDropzoneProps {
  file?: File;
  uploadProgress: number;
  isLoading: boolean;
  maxSizeInBytes: number;
  onDrop: (files: File[]) => void;
  onReject: (fileRejections: FileRejection[]) => void;
}

function getActiveColor(status: DropzoneStatus, theme: MantineTheme): string {
  if (status.accepted) {
    return theme.colors[theme.primaryColor][6];
  }

  if (status.rejected) {
    return theme.colors.red[6];
  }

  return theme.black;
}

const SecureDropzone: React.FC<SecureDropzoneProps> = ({
  file,
  uploadProgress,
  isLoading,
  maxSizeInBytes,
  onDrop,
  onReject,
}) => {
  const theme = useMantineTheme();
  const openRef = useRef<() => void>(null);

  const maxSizeInMb = maxSizeInBytes / (1024 * 1024);

  const getUploadStatusText = (): string => {
    if (uploadProgress === 100) {
      return "Upload complete!";
    }
    if (uploadProgress > 0) {
      return "File uploading...";
    }
    if (isLoading) {
      return "Preparing upload...";
    }
    return file?.name || "";
  };

  const getStatus = (status: DropzoneStatus): string => {
    if (status.accepted) {
      return "Drop files here";
    }
    if (status.rejected) {
      return `File must be less than ${maxSizeInMb}MB`;
    }
    return "Upload file";
  };

  const description = (
    <>
      Drag and drop files here or select to upload.
      <br />
      Files must be .CSV, .XLSX or .PDF. Max file size is {maxSizeInMb}MB.
    </>
  );

  return (
    <div className="flex-1 mt-8 relative">
      <Dropzone
        loading={false}
        openRef={openRef}
        onDrop={onDrop}
        className="h-full border border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center relative"
        radius="md"
        accept={[MIME_TYPES.pdf, MIME_TYPES.csv, MIME_TYPES.xlsx]}
        maxSize={maxSizeInBytes}
        onReject={onReject}
      >
        {(status) => (
          <div
            className="flex flex-col items-center justify-center h-full w-full"
            style={{ pointerEvents: "none" }}
          >
            {!file ? (
              <>
                <CloudUpload size={50} color={getActiveColor(status, theme)} />
                <Text
                  align="center"
                  weight={700}
                  size="xl"
                  sx={{ color: getActiveColor(status, theme) }}
                  className="mt-4"
                >
                  {getStatus(status)}
                </Text>
                <Text align="center" className="mt-2 mb-5">
                  {description}
                </Text>
                <Button
                  size="md"
                  radius="sm"
                  color="jem-theme"
                  style={{ pointerEvents: "auto" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openRef.current?.();
                  }}
                >
                  Select File
                </Button>
              </>
            ) : (
              <>
                {uploadProgress < 100 ? (
                  <CloudUpload
                    size={50}
                    color={getActiveColor(status, theme)}
                  />
                ) : (
                  <FileText size={50} color={getActiveColor(status, theme)} />
                )}
                <Text
                  align="center"
                  weight={700}
                  size="xl"
                  sx={{ color: getActiveColor(status, theme) }}
                  className="mt-4"
                >
                  {getUploadStatusText()}
                </Text>
                {uploadProgress < 100 && (
                  <Progress
                    value={uploadProgress}
                    color="#FFBEC8"
                    size="sm"
                    className="mt-4"
                    style={{ width: "300px" }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </Dropzone>
    </div>
  );
};

export default SecureDropzone;
